import { params } from "@/params";
import { useAuthorizedQuery, useAuthorizedMutation } from "..";
import { useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { IdToken } from "@auth0/auth0-spa-js";
import { FeatureAccessContext } from "@/context/featureAccess";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

const ARTICLE_IDS_CHUNK_SIZE = 20;

export const useArticles = ({
  enabled,
  findBy,
  startDate,
  endDate,
  page,
}: {
  enabled: boolean;
  findBy?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
}) => {
  const accessLabelsEnabled = useFlag("articleAccessLabels");

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();

    const paramMappings = [
      { value: startDate, key: "startDate" },
      { value: endDate, key: "endDate" },
      { value: findBy, key: "search" },
      { value: page?.toString(), key: "page" },
      { value: accessLabelsEnabled, key: "includeAccessLabels" },
    ];

    paramMappings.forEach(({ value, key }) => {
      if (value) params.append(key, value.toString());
    });

    return params.toString();
  }, [findBy, startDate, endDate, page, accessLabelsEnabled]);

  return useAuthorizedQuery({
    queryKey: `useGetArticles-${queryParams}`,
    url: `${params.API_URL}/knowledge-base-handler/articles${
      queryParams ? `?${queryParams}` : ""
    }`,
    method: "GET",
    enabled,
  });
};

export const useDeleteArticle = () => {
  return useAuthorizedMutation<void, { articleId: string }>({
    url: `${params.API_URL}/knowledge-base-handler/knowledge/articles`,
    method: "DELETE",
  });
};

type ArticleUsageEntry = { relatedTickets: number; ticketTrend: number };
type ArticleUsageResponse = { ticketsCount: Record<string, ArticleUsageEntry> };

export const useArticlesUsage = ({
  enabled,
  startDate,
  endDate,
  articleIds,
}: {
  enabled: boolean;
  startDate?: string;
  endDate?: string;
  articleIds?: string[];
}) => {
  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { selectedWorkspace } = useContext(FeatureAccessContext);

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  const chunks = useMemo(() => {
    const result: string[][] = [];
    if (!articleIds) return result;
    for (let i = 0; i < articleIds.length; i += ARTICLE_IDS_CHUNK_SIZE) {
      result.push(articleIds.slice(i, i + ARTICLE_IDS_CHUNK_SIZE));
    }
    return result;
  }, [articleIds]);

  const queryKey = useMemo(
    () =>
      `useArticlesUsage-${startDate ?? ""}-${endDate ?? ""}-${
        articleIds?.join(",") ?? ""
      }-${selectedWorkspace ?? ""}`,
    [startDate, endDate, articleIds, selectedWorkspace]
  );

  return useQuery<ArticleUsageResponse>({
    queryKey: [queryKey],
    queryFn: async () => {
      const authToken = tokenId?.__raw ?? "";
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      };
      if (selectedWorkspace) headers["x-tenant-id"] = selectedWorkspace;

      const responses = await Promise.all(
        chunks.map((chunk) => {
          const qp = new URLSearchParams();
          if (startDate) qp.append("startDate", startDate);
          if (endDate) qp.append("endDate", endDate);
          chunk.forEach((id) => qp.append("articleIds", id));
          const url = `${params.API_URL}/platform/tickets/count/by-article-ids?${qp.toString()}`;
          return fetch(url, { method: "GET", headers }).then((r) => r.json());
        })
      );

      const merged: Record<string, ArticleUsageEntry> = {};
      for (const res of responses) {
        if (res?.ticketsCount) Object.assign(merged, res.ticketsCount);
      }
      return { ticketsCount: merged };
    },
    enabled: enabled && isAuthenticated && !!tokenId && chunks.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};
